import type { ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Info,
  Loader2,
  Lock,
  Minus,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

import {
  BankConfirmationSection,
  type BankConfirmationEntry,
} from '@/components/bank-entries/BankConfirmationSection';
import { FundCostInvoiceAttachButton } from '@/components/fund-costs/FundCostInvoiceAttachButton';
import {
  FundTransactionDocumentList,
  UnlinkFundTransactionDocumentButton,
} from '@/components/transactions/FundTransactionDocumentList';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAssetTransaction } from '@/hooks/react-query/useAssetTransactions';
import { useTransactionDocuments } from '@/hooks/react-query/useCapTable';
import { useDownloadDocument } from '@/hooks/react-query/useDocuments';
import {
  useAdminApproveFundTransaction,
  useDeleteFundTransactionBankEntry,
  useFundTransactionBankMatching,
} from '@/hooks/react-query/useFundTransactions';
import {
  isBooked,
  type BankEntryResponse,
  type ReconciliationStateResponse,
  type SlotState,
} from '@/types/fund-transactions';
import { formatDate } from '@/utils/formatters';

const STALE_DRAFT_DAYS = 14;

interface FundTransactionVerificationCardProps {
  fundId: string;
  transactionId: string;
  state: ReconciliationStateResponse;
  canEdit: boolean;
  draftCreatedAt: string;
  bankEntries: BankEntryResponse[];
  transactionAmount: number;
  transactionCurrency: string;
  onAddBank?: () => void;
  onEditBank: (entry: BankEntryResponse) => void;
  onMatchBankLine: () => void;
  onManualApprove: () => void;
  renderDocument: (slot: SlotState) => ReactNode;
  bankLines?: ReactNode;
}

export function FundTransactionVerificationCard({
  fundId,
  transactionId,
  state,
  canEdit,
  draftCreatedAt,
  bankEntries,
  transactionAmount,
  transactionCurrency,
  onAddBank,
  onEditBank,
  onMatchBankLine,
  onManualApprove,
  renderDocument,
  bankLines,
}: FundTransactionVerificationCardProps) {
  // "Approvable" covers DRAFT (management fees) and ISSUED (distributions, after
  // notices have been sent). Both states still accept bank entries and admin/manual
  // approval into APPROVED.
  const isDraft = state.status === 'DRAFT' || state.status === 'ISSUED';
  const isPastDraft = isBooked(state.status);
  const isReady =
    state.threshold > 0 && state.verified_source_count >= state.threshold;

  const slotsBySource = new Map(state.slots.map((s) => [s.source, s]));
  const fundSlot = slotsBySource.get('fund_input');
  const legalDocSlot = slotsBySource.get('legal_doc');
  const bankSlot = slotsBySource.get('bank');
  const hasSecondSource = !!fundSlot?.required || !!legalDocSlot?.required;
  const isBankOnlyVerification = !hasSecondSource;
  const secondSourceRequirement = legalDocSlot
    ? 'A supporting document and bank confirmation must both be verified before this transaction is posted to the general ledger.'
    : 'Both sources - fund input and bank - must confirm this transaction before it is posted to the general ledger.';

  const staleDays =
    isDraft && draftCreatedAt ? daysSince(draftCreatedAt) : null;
  const isStale = staleDays != null && staleDays >= STALE_DRAFT_DAYS;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            Verification
            {isPastDraft && state.manual_approval !== null && (
              <Badge variant="secondary" className="gap-1 font-normal">
                <ShieldAlert className="h-3 w-3 text-amber-500 dark:text-amber-400" />
                Manual override
              </Badge>
            )}
            {isPastDraft && state.manual_approval === null && (
              <Badge variant="secondary" className="gap-1 font-normal">
                <ShieldCheck className="h-3 w-3 text-emerald-500 dark:text-emerald-400" />
                Verified
              </Badge>
            )}
            {!canEdit && isDraft && (
              <Badge
                variant="secondary"
                className="gap-1 font-normal"
                title="Read-only"
              >
                <Lock className="h-3 w-3" />
                Locked
              </Badge>
            )}
          </CardTitle>
          {isDraft && state.threshold > 0 && (
            <span
              className={`text-xs ${
                isReady
                  ? 'text-emerald-600 font-medium'
                  : 'text-muted-foreground'
              }`}
            >
              {state.verified_source_count} of {state.threshold} source
              {state.threshold === 1 ? '' : 's'} confirmed
              {isReady ? ' - ready for admin review' : ''}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {isBankOnlyVerification
            ? 'Bank confirmation is required before this transaction is posted to the general ledger.'
            : secondSourceRequirement}
        </p>
        {isStale && !isReady && (
          <div className="flex items-start gap-2 pt-1 text-xs text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
            <span>
              <span className="font-medium">
                Draft is {staleDays} days old.
              </span>{' '}
              A source is still missing. Link it manually, or ask an admin to
              approve with a recorded reason.
            </span>
          </div>
        )}
      </CardHeader>

      <CardContent className="p-0 divide-y">
        {!isBankOnlyVerification && fundSlot && (
          <FundInputBlock slot={fundSlot} />
        )}
        {legalDocSlot && renderDocument(legalDocSlot)}
        {bankSlot && (
          <BankBlock
            slot={bankSlot}
            canEdit={canEdit}
            bankEntries={bankEntries}
            onAddBank={onAddBank}
            onEditBank={onEditBank}
            onMatchBankLine={onMatchBankLine}
            fundId={fundId}
            transactionId={transactionId}
            transactionAmount={transactionAmount}
            transactionCurrency={transactionCurrency}
            lines={bankLines}
          />
        )}
        <ApprovalActionsBlock
          fundId={fundId}
          transactionId={transactionId}
          state={state}
          isDraft={isDraft}
          isPastDraft={isPastDraft}
          isReady={isReady}
          onManualApprove={onManualApprove}
        />
      </CardContent>
    </Card>
  );
}

function SourceStatusBadge({ slot }: { slot: SlotState | undefined }) {
  if (!slot) return null;
  if (slot.verified) {
    return (
      <Badge
        variant="secondary"
        className="bg-emerald-50 text-emerald-700 border-emerald-200 font-normal dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900"
      >
        <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-600 dark:text-emerald-400" />
        Verified
      </Badge>
    );
  }
  if (!slot.required) {
    return (
      <Badge variant="outline" className="font-normal">
        <Minus className="h-3 w-3 mr-1" />
        Optional
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="border-amber-200 bg-amber-50 text-amber-800 font-normal dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300"
    >
      <Clock className="h-3 w-3 mr-1" />
      Pending
    </Badge>
  );
}

function FundInputBlock({ slot }: { slot: SlotState | undefined }) {
  if (!slot || !slot.required) return null;

  return (
    <section className="px-6 py-5">
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Fund input</h3>
        <SourceStatusBadge slot={slot} />
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        A structured fund record (fee schedule, drawdown notice, distribution
        declaration) confirms this transaction from the fund side.
      </p>

      {slot.verified ? (
        <div className="mt-3 flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <div className="text-sm">
            <p className="font-medium">
              {slot.artefact_label ?? 'Fund input linked'}
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex items-start gap-2">
          <Clock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            Awaiting fund input. The originating scheduler run will confirm this
            source automatically.
          </span>
        </div>
      )}
    </section>
  );
}

export function LegalDocBlock({
  slot,
  companyId,
  assetSaleId,
  ledgerTransactionId: directLedgerTransactionId,
}: {
  slot: SlotState | undefined;
  companyId?: string;
  assetSaleId?: string | null;
  ledgerTransactionId?: string | null;
}) {
  const downloadDocument = useDownloadDocument();
  // Captable-ledger fund transactions (financing round / issuance) carry the ledger
  // transaction id directly. Asset-sale fund transactions only have the asset-sale id, so
  // resolve its ledger transaction first. Skip the asset lookup in the former.
  const { data: assetTxn, isLoading: assetLoading } = useAssetTransaction(
    slot?.required && !directLedgerTransactionId
      ? (assetSaleId ?? undefined)
      : undefined
  );
  const ledgerTransactionId =
    directLedgerTransactionId ?? assetTxn?.ledger_transaction_id ?? null;
  const { data: documents = [], isLoading: docsLoading } =
    useTransactionDocuments(
      companyId ?? '',
      ledgerTransactionId ?? '',
      !!slot?.required && !!companyId && !!ledgerTransactionId
    );
  const isLoading = assetLoading || docsLoading;

  if (!slot || !slot.required) return null;

  return (
    <section className="px-6 py-5">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Legal document</h3>
        <SourceStatusBadge slot={slot} />
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        A signed agreement (SPA, SHA, board resolution) must be attached to the
        transaction on the portfolio company side.
      </p>

      {!isLoading && documents.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {documents.map((doc) => (
            <li key={doc.id} className="flex items-center gap-2">
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm font-medium truncate">
                {doc.document_name}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 ml-auto shrink-0"
                onClick={() =>
                  downloadDocument.mutate({
                    id: doc.document_id,
                    documentName: doc.document_original_filename,
                  })
                }
                disabled={downloadDocument.isPending}
                aria-label="Download document"
              >
                {downloadDocument.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <LegalDocumentPlaceholder
          isLoading={isLoading}
          isVerified={slot.verified}
        />
      )}
    </section>
  );
}

function LegalDocumentPlaceholder({
  isLoading,
  isVerified,
}: {
  isLoading: boolean;
  isVerified: boolean;
}) {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground mt-3">Loading…</p>;
  }
  if (isVerified) {
    return (
      <div className="mt-3 flex items-start gap-2">
        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span className="text-sm font-medium">Legal document verified</span>
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-start gap-2">
      <Clock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">
        Awaiting legal document. Attach a signed agreement to the transaction on
        the portfolio company side.
      </span>
    </div>
  );
}

export function FundDocumentBlock({
  slot,
  fundId,
  transactionId,
  canEdit,
}: {
  slot: SlotState;
  fundId: string;
  transactionId: string;
  canEdit: boolean;
}) {
  return (
    <section className="px-6 py-5">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Document</h3>
        <SourceStatusBadge slot={slot} />
        <div className="ml-auto">
          <FundCostInvoiceAttachButton
            fundId={fundId}
            transactionId={transactionId}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        {slot.required
          ? 'The invoice behind this payment. Its type tag verifies the document source.'
          : 'The invoice behind this payment. Optional, and can be attached after posting.'}
      </p>

      <FundTransactionDocumentList
        fundId={fundId}
        transactionId={transactionId}
        emptyState={<FundDocumentEmptyState slot={slot} />}
        renderRowActions={(doc) =>
          canEdit && (
            <UnlinkFundTransactionDocumentButton
              fundId={fundId}
              transactionId={transactionId}
              linkId={doc.id}
            />
          )
        }
      />
    </section>
  );
}

function FundDocumentEmptyState({ slot }: { slot: SlotState }) {
  if (slot.verified) {
    return (
      <div className="mt-3 flex items-start gap-2">
        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span className="text-sm font-medium">Document verified</span>
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-start gap-2">
      <Clock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">
        {slot.required
          ? 'Awaiting the invoice. Attach it to verify this source.'
          : 'No invoice attached yet.'}
      </span>
    </div>
  );
}

function BankBlock({
  slot,
  canEdit,
  bankEntries,
  onAddBank,
  onEditBank,
  onMatchBankLine,
  fundId,
  transactionId,
  transactionAmount,
  transactionCurrency,
  lines,
}: {
  slot: SlotState | undefined;
  canEdit: boolean;
  bankEntries: BankEntryResponse[];
  onAddBank?: () => void;
  onEditBank: (entry: BankEntryResponse) => void;
  onMatchBankLine: () => void;
  fundId: string;
  transactionId: string;
  transactionAmount: number;
  transactionCurrency: string;
  lines?: ReactNode;
}) {
  const deleteEntry = useDeleteFundTransactionBankEntry(fundId, transactionId);
  // Also tells us whether a provider feed has anything to offer, so the match
  // action is only shown where it can lead somewhere.
  const { data: matching } = useFundTransactionBankMatching(
    fundId,
    transactionId,
    !!slot?.required
  );

  if (!slot) return null;

  const normalized: BankConfirmationEntry[] = bankEntries.map((entry) => ({
    id: entry.id,
    value_date: entry.value_date,
    amount: entry.amount,
    currency: entry.currency,
    counterparty_iban: entry.counterparty_iban,
    payment_reference: entry.payment_reference,
    source: entry.source,
    source_drawdown_payment_id: entry.source_drawdown_payment_id,
    created_by: entry.created_by,
    created_by_name: entry.created_by_name,
    created_at: entry.created_at,
  }));

  return (
    <BankConfirmationSection
      required={slot.required}
      entries={normalized}
      canEdit={canEdit}
      isDeleting={deleteEntry.isPending}
      expectedAmount={transactionAmount}
      expectedCurrency={transactionCurrency}
      verified={slot.verified}
      matchedLines={matching?.matched_lines ?? []}
      onMatchBankLine={matching?.is_reconcilable ? onMatchBankLine : undefined}
      onAdd={onAddBank}
      onEdit={(entryId) => {
        const entry = bankEntries.find((e) => e.id === entryId);
        if (entry) onEditBank(entry);
      }}
      onDelete={(entryId) => deleteEntry.mutate(entryId)}
    >
      {lines}
    </BankConfirmationSection>
  );
}

function ApprovalActionsBlock({
  fundId,
  transactionId,
  state,
  isDraft,
  isPastDraft,
  isReady,
  onManualApprove,
}: {
  fundId: string;
  transactionId: string;
  state: ReconciliationStateResponse;
  isDraft: boolean;
  isPastDraft: boolean;
  isReady: boolean;
  onManualApprove: () => void;
}) {
  const adminApprove = useAdminApproveFundTransaction(fundId, transactionId);

  const canApproveNow = isDraft && isReady && state.can_manual_approve;
  const canManualOverride = isDraft && !isReady && state.can_manual_approve;
  const awaitingAdmin = isDraft && isReady && !state.can_manual_approve;
  const manualRecord = isPastDraft && state.manual_approval !== null;

  if (!canApproveNow && !canManualOverride && !awaitingAdmin && !manualRecord) {
    return null;
  }

  return (
    <section className="px-6 py-5 space-y-3">
      {manualRecord && state.manual_approval && (
        <div className="flex items-start gap-2 text-sm">
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-amber-500 dark:text-amber-400" />
          <div>
            <p>
              Manually approved by{' '}
              <span className="font-medium">
                {state.manual_approval.actor_name ?? 'admin'}
              </span>{' '}
              on{' '}
              <span className="font-medium">
                {formatDate(state.manual_approval.approved_at)}
              </span>
              .
            </p>
            {state.manual_approval.reason && (
              <p className="text-sm text-muted-foreground italic mt-1">
                &ldquo;{state.manual_approval.reason}&rdquo;
              </p>
            )}
          </div>
        </div>
      )}

      {canApproveNow && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-2">
            <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <p className="text-sm">
              <span className="font-medium">
                All required sources confirmed.
              </span>{' '}
              <span className="text-muted-foreground">
                Admin sign-off posts this transaction to the general ledger.
              </span>
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => adminApprove.mutate()}
            disabled={adminApprove.isPending}
            data-testid="approve-button"
          >
            {adminApprove.isPending ? 'Approving…' : 'Approve'}
          </Button>
        </div>
      )}

      {awaitingAdmin && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
          All required sources confirmed. Awaiting admin approval.
        </div>
      )}

      {canManualOverride && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Approve manually with a recorded reason.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={onManualApprove}
            data-testid="manual-approve-button"
          >
            <ShieldAlert className="h-4 w-4 mr-2" />
            Approve
          </Button>
        </div>
      )}
    </section>
  );
}

function daysSince(iso: string): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  const diffMs = Date.now() - then;
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}
